import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { Button, Col } from "@/components";
import "./DelegateFilterDropDown.scss";
import { Close } from "@/icons";

export type RelatedPeople = {
  birthDate: string;
  firstName: string;
  fiscalCode: string;
  lastName: string;
  relationship: string;
};

type RelatedPeopleDropDownProps = {
  size?: "small" | "large";
  items: RelatedPeople[];
  className?: string;
  selectedPerson: RelatedPeople | null;
  setSelectedPerson: (person: RelatedPeople | null) => void;
  placeholder?: string;
  btnText?: string;
};

export const DelegateFilterDropDown = ({
  size = "large",
  items = [],
  className,
  selectedPerson,
  setSelectedPerson,
  placeholder = "",
  btnText = "Cerca",
}: RelatedPeopleDropDownProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredItems = items.filter((person) => {
    const fullName = `${person.firstName} ${person.lastName} ${person.relationship}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded]);

  useEffect(() => {
    if (isExpanded) {
      setSearchTerm("");
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [isExpanded]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isExpanded) {
      setSearchTerm(e.target.value);
    }
  };

  const handleInputClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPerson(null);
    setSearchTerm("");
  };

  const displayValue =
    !isExpanded && selectedPerson
      ? `${selectedPerson.firstName} ${selectedPerson.lastName} (${selectedPerson.relationship})`
      : searchTerm;

  const showCloseButton = selectedPerson !== null || searchTerm.length > 0;

  const highlightMatch = (text: string, query: string) => {
    if (!query || query.trim() === "")
      return (
        <span style={{ color: "black", fontSize: "0.85rem" }}> {text} </span>
      );

    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;

    const beforeMatch = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const afterMatch = text.slice(index + query.length);

    return (
      <span style={{ color: "black", fontSize: "0.85rem" }}>
        {beforeMatch}
        <span style={{ fontSize: "0.85rem" }} className="text-primary fw-bold">
          {match}
        </span>
        {afterMatch}
      </span>
    );
  };

  return (
    <div
      ref={dropdownRef}
      className={clsx(
        "ui-related-dropdown",
        "br-x4 bg-border-secondary w-full",
        className,
      )}
      style={size === "large" ? { maxWidth: "100%" } : { maxWidth: "553px" }}
    >
      <div className="ui-input-container">
        <input
          ref={inputRef}
          className={clsx(
            "ui-related-dropdown-input",
            "bg-white fs-6 br-x4 w-full pl-x24",
            { active: isExpanded },
          )}
          value={displayValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          onClick={handleInputClick}
        />

        {showCloseButton && (
          <div className="ui-close-button" onClick={handleClearClick}>
            <Close width={20} />
          </div>
        )}

        <div className="ui-div-btn">
          <Button
            className={clsx(
              "ui-btn-search",
              "bg-primary text-white fs-7 fw-semibold br-x4",
            )}
            onClick={handleInputClick}
          >
            {btnText}
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="ui-related-dropdown-options">
          <div className="px-x24 shadow w-full">
            <Col
              gap={"x4"}
              text={"primary"}
              fs={"custom"}
              fw={"regular"}
              className={"ui-options-list fw-regular"}
            >
              {filteredItems.length > 0 ? (
                filteredItems.map((person, index) => {
                  const fullName = `${person.firstName} ${person.lastName} (${person.relationship})`;
                  return (
                    <div
                      className="ui-option-item"
                      key={person.fiscalCode || index}
                      onClick={() => {
                        setSelectedPerson(person);
                        setIsExpanded(false);
                      }}
                    >
                      {highlightMatch(fullName, searchTerm)}
                    </div>
                  );
                })
              ) : (
                <div className="ui-option-item no-results">
                  <span style={{ color: "gray" }}>
                    Nessun risultato trovato
                  </span>
                </div>
              )}
            </Col>
          </div>
        </div>
      )}
    </div>
  );
};
